import { useParams, Link } from 'react-router-dom';
import { getShowById, shows } from '@/mocks/shows';
import type { Show } from '@/mocks/shows';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const show: Show | undefined = id ? getShowById(parseInt(id)) : undefined;

  if (!show) {
    return (
      <main className="min-h-screen bg-background-50 pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 rounded-2xl bg-background-100">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-semibold text-foreground-950 mb-2">未找到该剧集</h2>
          <p className="text-foreground-500 mb-6">该内容可能已被下架或链接无效</p>
          <Link
            to="/"
            className="px-6 py-2.5 text-sm font-semibold rounded-lg bg-primary-500 text-background-950 hover:bg-primary-400 transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedShows = shows
    .filter(s => s.id !== show.id && (s.categorySlug === show.categorySlug || s.tags.some(t => show.tags.includes(t))))
    .slice(0, 6);

  return (
    <main className="min-h-screen bg-background-50 pt-16">
      {/* Breadcrumb */}
      <nav className="px-4 md:px-8 py-4 max-w-[1280px] mx-auto" aria-label="面包屑导航">
        <ol className="flex items-center gap-2 text-sm flex-wrap">
          <li>
            <Link to="/" className="text-foreground-500 hover:text-primary-400 transition-colors duration-200 whitespace-nowrap">
              首页
            </Link>
          </li>
          <li className="text-foreground-300">
            <i className="ri-arrow-right-s-line text-xs"></i>
          </li>
          <li>
            <Link to={`/category/${show.categorySlug}`} className="text-foreground-500 hover:text-primary-400 transition-colors duration-200 whitespace-nowrap">
              {show.category}
            </Link>
          </li>
          <li className="text-foreground-300">
            <i className="ri-arrow-right-s-line text-xs"></i>
          </li>
          <li>
            <span className="text-foreground-800 font-medium whitespace-nowrap">{show.title}</span>
          </li>
        </ol>
      </nav>

      {/* Banner */}
      <section className="relative w-full h-[300px] md:h-[450px] overflow-hidden">
        <img
          src={show.bannerImage}
          alt={show.title}
          className="w-full h-full object-cover"
          title={`${show.title} - ${show.englishTitle}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-transparent"></div>
      </section>

      {/* Content */}
      <section className="px-4 md:px-8 max-w-[1280px] mx-auto -mt-32 md:-mt-40 relative z-10">
        <div className="flex flex-col md:flex-row gap-6 md:gap-10">
          {/* Cover */}
          <div className="flex-shrink-0 w-40 md:w-56 mx-auto md:mx-0">
            <div className="aspect-[2/3] rounded-lg overflow-hidden">
              <img
                src={show.coverImage}
                alt={show.title}
                className="w-full h-full object-cover"
                title={`${show.title} - ${show.englishTitle}`}
              />
            </div>
          </div>

          {/* Info */}
          <div className="flex-1">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
              {show.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-500 mt-2">
              {show.englishTitle}
            </p>

            <div className="flex flex-wrap items-center gap-3 mt-4">
              <span className="px-2.5 py-1 text-xs rounded-full bg-primary-500/20 text-primary-400 whitespace-nowrap">
                ★ {show.rating}
              </span>
              <span className="px-2.5 py-1 text-xs rounded-full bg-background-200 text-foreground-500 whitespace-nowrap">
                {show.category}
              </span>
              <span className="px-2.5 py-1 text-xs rounded-full bg-background-200 text-foreground-500 whitespace-nowrap">
                {show.year}
              </span>
              <span className="px-2.5 py-1 text-xs rounded-full bg-background-200 text-foreground-500 whitespace-nowrap">
                {show.currentEpisode}
              </span>
              <span className="px-2.5 py-1 text-xs rounded-full bg-background-200 text-foreground-500 whitespace-nowrap">
                {show.status}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-4">
              {show.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 text-xs rounded-full bg-secondary-500/10 text-secondary-400 whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-6">
              <button className="px-8 py-3 text-sm font-semibold rounded-lg bg-primary-500 text-background-950 hover:bg-primary-400 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2">
                <i className="ri-play-fill"></i>
                立即播放
              </button>
              <button className="px-6 py-3 text-sm rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2">
                <i className="ri-heart-line"></i>
                收藏
              </button>
              <button className="px-6 py-3 text-sm rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2">
                <i className="ri-share-forward-line"></i>
                分享
              </button>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="mt-10 max-w-4xl">
          <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-4 flex items-center gap-3" style={{ fontFamily: 'var(--font-heading)' }}>
            <div className="w-1 h-5 rounded-full bg-primary-500"></div>
            剧情简介
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
            {show.longDescription}
          </p>
        </div>

        {/* Cast & Director */}
        <div className="mt-10 max-w-4xl">
          <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-4 flex items-center gap-3" style={{ fontFamily: 'var(--font-heading)' }}>
            <div className="w-1 h-5 rounded-full bg-accent-500"></div>
            演职人员
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg bg-background-100">
              <p className="text-xs text-foreground-400 mb-1">导演</p>
              <p className="text-sm font-semibold text-foreground-800">{show.director}</p>
            </div>
            {show.cast.map((actor, i) => (
              <div key={i} className="p-4 rounded-lg bg-background-100">
                <p className="text-xs text-foreground-400 mb-1">{i === 0 ? '主演' : '演员'}</p>
                <p className="text-sm font-semibold text-foreground-800">{actor}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Related Shows */}
        {relatedShows.length > 0 && (
          <div className="mt-14 pb-14 max-w-4xl">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-6 flex items-center gap-3" style={{ fontFamily: 'var(--font-heading)' }}>
              <div className="w-1 h-5 rounded-full bg-primary-500"></div>
              猜你喜欢
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 md:gap-4">
              {relatedShows.map((s) => (
                <Link
                  key={s.id}
                  to={`/detail/${s.id}`}
                  className="group block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200 transition-all duration-300 cursor-pointer"
                  data-product-shop
                >
                  <div className="relative aspect-[2/3] overflow-hidden">
                    <img
                      src={s.coverImage}
                      alt={s.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      title={s.title}
                    />
                    <div className="absolute top-2 right-2">
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-background-950/70 text-primary-400 whitespace-nowrap">
                        {s.rating}
                      </span>
                    </div>
                  </div>
                  <div className="p-2.5">
                    <h3 className="text-xs font-semibold text-foreground-950 group-hover:text-primary-400 transition-colors duration-200 line-clamp-1 whitespace-nowrap overflow-hidden text-ellipsis">
                      {s.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Movie",
            "name": show.title,
            "alternateName": show.englishTitle,
            "description": show.description,
            "dateCreated": `${show.year}`,
            "director": { "@type": "Person", "name": show.director },
            "actor": show.cast.map(name => ({ "@type": "Person", "name": name })),
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": show.rating,
              "bestRating": "10",
              "worstRating": "0"
            },
            "genre": show.tags,
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://aiyaaiya.com/" },
                { "@type": "ListItem", "position": 2, "name": show.category, "item": `https://aiyaaiya.com/category/${show.categorySlug}` },
                { "@type": "ListItem", "position": 3, "name": show.title }
              ]
            }
          })
        }}
      />
    </main>
  );
}